import { ActionEnum } from "./constants/ActionEnum";
import RAW_ACCOUNTS from "./data/diagnoses.json";

/* ---- Branding ---- */
/* Change these two lines to rename the app everywhere.
   Logo lives in components/Logo.jsx. */
export const APP_NAME = "Re-Engage";
export const APP_TAGLINE = "Retention that acts, not notifies.";

/* Single source of demo data. No backend, swap these exports for API calls later.
   Shapes follow the taxonomy in track/track.md. */

/* ---- Reference tables (the decision layer, as data not code) ---- */

export const DIAGNOSES = {
  UNRESOLVED_FRICTION: {
    id: "UNRESOLVED_FRICTION",
    label: "Unresolved friction",
    blurb: "Told us, still stuck",
    needState: "COMPETENCE",
    lever: "Route to fix / workaround",
    costToBusiness: "Engineering time",
  },
  UNREALISED_VALUE: {
    id: "UNREALISED_VALUE",
    label: "Unrealised value",
    blurb: "Paying, never activated",
    needState: "COMPETENCE",
    lever: "Hands-on setup, done-for-you",
    costToBusiness: "CSM hours",
  },
  WRONG_FIT: {
    id: "WRONG_FIT",
    label: "Wrong fit",
    blurb: "Paying for the wrong thing",
    needState: "AUTONOMY",
    lever: "Proactively downgrade them",
    costToBusiness: "Revenue, now",
  },
  VALUE_DOUBT: {
    id: "VALUE_DOUBT",
    label: "Value doubt",
    blurb: "Usage fine, renewal near",
    needState: "AUTONOMY",
    lever: "Show realised ROI",
    costToBusiness: "Reporting effort",
  },
  RELATIONSHIP_GAP: {
    id: "RELATIONSHIP_GAP",
    label: "Relationship gap",
    blurb: "No human contact",
    needState: "RELATEDNESS",
    lever: "Named human, real ownership",
    costToBusiness: "Headcount",
  },
};

export const NEED_STATES = {
  COMPETENCE: {
    id: "COMPETENCE",
    label: "Competence",
    feeling: "“I can’t make this work”",
    fits: "Remove the obstacle: fix, workaround, done-for-you setup",
    backfires: "A discount, implies they’re cheap, and it’s still broken",
  },
  AUTONOMY: {
    id: "AUTONOMY",
    label: "Autonomy",
    feeling: "“I feel trapped / oversold”",
    fits: "Give control back: downgrade, pause, self-serve exit",
    backfires: "Retention pressure, deepens the trapped feeling",
  },
  RELATEDNESS: {
    id: "RELATEDNESS",
    label: "Relatedness",
    feeling: "“Nobody noticed me”",
    fits: "A named human taking ownership",
    backfires: "Automated drip, proves nobody noticed",
  },
};

/* Evidence weights, ordered by falsifiability. Text-only input, so every
   tier here is self-reported, see track.md 4. */
export const EVIDENCE_WEIGHTS = {
  STATED_GOAL_REPEATED: { label: "Stated unmet goal, repeated", weight: 5 },
  STATED_GOAL_SINGLE: { label: "Stated unmet goal, single mention", weight: 4 },
  ACCOUNT_MISMATCH: { label: "Contract/account mismatch", weight: 3 },
  ASPECT_SENTIMENT: { label: "Aspect-based sentiment", weight: 3 },
  EMOTION: { label: "Emotion classification", weight: 2 },
  GENERIC_SENTIMENT: { label: "Generic sentiment score", weight: 1 },
};

export const CONFIDENCE_TIERS = {
  /* Matches backend/taxonomy.py's CONFIDENCE_TIERS. Tuned to what
     scoring.py can actually produce from this dataset (repetition +
     emotion only) — max attainable score is 8, so HIGH must sit at or
     below that or the "Act" tier is permanently unreachable. */
  HIGH: { id: "HIGH", label: ActionEnum.ACT, badge: "bad", min: 8 },
  MEDIUM: { id: "MEDIUM", label: ActionEnum.REVIEW, badge: "warn", min: 6 },
  LOW: { id: "LOW", label: ActionEnum.LOG, badge: "good", min: 0 },
};

export const ACTIONS = {
  WORKAROUND: { label: "Micro-guide / workaround", cost: "Near zero", reversible: "Yes" },
  CSM_SETUP: { label: "CSM setup session", cost: "Staff hours", reversible: "Yes" },
  NAMED_OWNER: { label: "Named human ownership", cost: "Headcount", reversible: "Partly" },
  ENG_FIX: { label: "Engineering fix", cost: "Eng time (high)", reversible: "No" },
  DOWNGRADE: { label: "Proactive downgrade", cost: "Revenue, immediately", reversible: "No" },
  DISCOUNT: { label: "Discount", cost: "Margin, recurring", reversible: "Hard to unwind" },
};

/* ---- Accounts ----
   Sourced from data/diagnoses.json (698 real customers, generated by the
   emotion model + Gemini diagnosis pipeline in backend/). `confidence_score`
   is renamed to `confidence` to match what the rest of this file and the
   page components expect; every other field is passed through unchanged.

   `diagnosis` is null for accounts where no stated cause matched any of the
   five taxonomy classes (~48% of accounts) — this is the documented
   "fixed taxonomy blind spot" from futureworks.md, not missing data.
   Components reading `a.diagnosis` must handle the null case. */

/* The pipeline's own `tier` field disagrees with the app's confidence
   thresholds on ~34% of records (e.g. tier: HIGH at score 8, when HIGH
   requires 11+) — recompute it locally so the badge always matches the
   evidence-bar verdict underneath it. */
export const ACCOUNTS = RAW_ACCOUNTS.map((a) => {
  const confidence = a.confidence_score;
  const tier =
    confidence >= CONFIDENCE_TIERS.HIGH.min
      ? "HIGH"
      : confidence >= CONFIDENCE_TIERS.MEDIUM.min
      ? "MEDIUM"
      : "LOW";

  /* scoring.py adds a silent +1 to the confidence score when the emotion
     reading is high-intensity (>=75%), on top of the listed evidence
     weights. That point never appears as its own evidence line, so the
     visible segments (e.g. 5 + 2) don't sum to the score shown (8). Rather
     than invent a fake extra evidence item, attribute the point to where
     it actually comes from: bump the weight on the EMOTION entry itself,
     since that's the reading the bonus is based on. */
  const baseWeight = (t) => EVIDENCE_WEIGHTS[t]?.weight ?? 0;
  const evidenceSum = (a.evidence || []).reduce(
    (sum, e) => sum + baseWeight(e.type),
    0
  );
  const bonus = confidence - evidenceSum;
  const evidence = (a.evidence || []).map((e) => ({
    ...e,
    weight: baseWeight(e.type),
  }));
  if (bonus > 0) {
    const emotionEntry = evidence.find((e) => e.type === "EMOTION");
    if (emotionEntry) emotionEntry.weight += bonus;
  }

  return { ...a, confidence, tier, evidence };
});

/* ---- Friction queue: accounts grouped by stated cause ---- */

export const FRICTION = [
  {
    id: "export-timeout",
    cause: "Export times out on large datasets",
    accountIds: ["acme", "globex"],
    accountCount: 17,
    arrAffected: 40000,
    trend: "growing",
    firstSeen: "2026-05-02",
  },
  {
    id: "sso-setup",
    cause: "SSO setup fails silently at step 3",
    accountIds: [],
    accountCount: 9,
    arrAffected: 23500,
    trend: "flat",
    firstSeen: "2026-04-18",
  },
  {
    id: "mobile-sync",
    cause: "Mobile app does not sync offline edits",
    accountIds: [],
    accountCount: 6,
    arrAffected: 11200,
    trend: "shrinking",
    firstSeen: "2026-03-27",
  },
];

/* ---- Impact / measurement (seeded) ---- */

export const IMPACT = {
  retainedRevenueNet: 128400,
  concessionCost: 21600,
  interventionsCommitted: 43,
  concessionBudget: { used: 21600, ceiling: 40000 },
  accountsUnderObservation: 12,

  /* Matched action vs generic-discount baseline, per diagnosis class. */
  matchedVsBaseline: [
    { diagnosis: "UNRESOLVED_FRICTION", matched: 71, baseline: 38 },
    { diagnosis: "WRONG_FIT", matched: 64, baseline: 29 },
    { diagnosis: "UNREALISED_VALUE", matched: 58, baseline: 41 },
    { diagnosis: "VALUE_DOUBT", matched: 52, baseline: 44 },
    { diagnosis: "RELATIONSHIP_GAP", matched: 61, baseline: 35 },
  ],

  repeatChurnSameCause: { before: 0.34, after: 0.11 },
};

/* ---- Derived helpers ---- */

export const diagnosisMix = () =>
  Object.values(DIAGNOSES)
    .map((d) => ({
      id: d.id,
      label: d.label,
      count: ACCOUNTS.filter((a) => a.diagnosis === d.id).length,
    }))
    .sort((a, b) => b.count - a.count);

export const getAccount = (id) => ACCOUNTS.find((a) => a.id === id);

export const confidenceTier = (score) =>
  score >= CONFIDENCE_TIERS.HIGH.min
    ? CONFIDENCE_TIERS.HIGH
    : score >= CONFIDENCE_TIERS.MEDIUM.min
    ? CONFIDENCE_TIERS.MEDIUM
    : CONFIDENCE_TIERS.LOW;

/* Ranked by expected value of intervening, not raw risk. */
export const rankedAccounts = () =>
  [...ACCOUNTS].sort((a, b) => b.confidence * b.ltv - a.confidence * a.ltv);

/* Auth moved to Supabase — see supabaseClient.js and pages/SignIn.jsx. */
